import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Box, Typography } from '@mui/material';

import type { ReportEmployer, ReportSubOwner } from '../../../../../../../../types';

import { TranslationKeys } from '../../../../../../../../translations';

import {
  ActionLink,
  ListCollapsibleRow,
  ListRow,
  ListRowNoData,
  ListRowSkeleton,
  Pagination,
} from '../../../../../../../../components';

import type { UserAccessesListOnChangeSelected, UserAccessesListSelectedData } from '../../UserAccessesList.types';

import { UserAccessesListHeader } from '../UserAccessesListHeader';
import { UserAccessesListContainer } from '../UserAccessesListContainer';
import { MuiCheckboxStyled } from '../../../../../../../../components/Checkbox/Checkbox.styles';
import { EditableFieldFactory } from '../../../../../../../../components/FormikField/Factory/EditableFieldFactory';

type UserAccessesListEmployersProps = {
  isLoading?: boolean;
  isLoggedInUser?: boolean;
  onChangeSelected: UserAccessesListOnChangeSelected;
  selectedData: UserAccessesListSelectedData | undefined;
  employers: Array<ReportEmployer> | undefined;
  selectedEventFlows: Array<string> | undefined;
  openConfirmationDialog: (value: boolean) => void;
  subOwners?: Array<ReportSubOwner>;
  page?: number;
  limit?: number;
  totalItems?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  searchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
  isSuperAdmin?: boolean;
};

const getEmployerWithSubEmployerIds = (employer: ReportEmployer): string[] => {
  const ids = [employer.employerId];
  employer.subEmployers?.forEach(subEmp => ids.push(subEmp.employerId));
  return ids;
};

/* const getAllEmployerIds = (employers: ReportEmployer[]): string[] => {
  const ids: string[] = [];
  employers.forEach(employer => {
    ids.push(employer.employerId);
    employer.subEmployers?.forEach(subEmp => ids.push(subEmp.employerId));
  });
  return ids;
}; */

const isEmployerSelectedHelper = (
  selectedData: UserAccessesListSelectedData | undefined,
  employerId: string,
): boolean => {
  const employers = selectedData?.employers;
  if (employers === '*') return true;
  if (typeof employers === 'object') return !!employers?.[employerId];
  return false;
};

const getEmployersAsObject = (
  employers: string | Record<string, string | Record<string, string>> | undefined,
): Record<string, string | Record<string, string>> => {
  if (employers === '*' || typeof employers === 'string') return {};
  return employers || {};
};

export const UserAccessesListEmployers = React.memo(function UserAccessesListEmployers({
  employers,
  selectedData,
  onChangeSelected,
  selectedEventFlows,
  openConfirmationDialog,
  isLoggedInUser = false,
  isLoading = false,
  subOwners,
  page = 1,
  limit = 25,
  totalItems = 0,
  totalPages = 0,
  onPageChange,
  onLimitChange,
  searchQuery: externalSearchQuery = '',
  onSearchQueryChange,
  isSuperAdmin = false,
}: UserAccessesListEmployersProps) {
  const searchQueryRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedDataRef = React.useRef(selectedData);
  const openConfirmationDialogRef = React.useRef(openConfirmationDialog);

  React.useEffect(() => {
    selectedDataRef.current = selectedData;
  }, [selectedData]);

  React.useEffect(() => {
    openConfirmationDialogRef.current = openConfirmationDialog;
  }, [openConfirmationDialog]);

  const listData = employers;

  const selectedEmployerIds =
    selectedData?.employers === '*'
      ? employers?.flatMap(emp => [emp.employerId, ...(emp.subEmployers?.map(s => s.employerId) || [])]) || []
      : Object.keys((selectedData?.employers as Record<string, string>) || {});
  const disabledIfLoadingOrIsLoggedInUser = isLoggedInUser || isLoading;
  const hasData = listData && listData?.length > 0;
  const showData = !isLoading && hasData;
  const showNoData = !isLoading && !showData;

  const onPaginationChange = React.useCallback(
    ({ page: newPage, rowSize }: { page?: number; rowSize?: number }) => {
      if (newPage !== undefined) {
        onPageChange?.(newPage);
      }
      if (rowSize !== undefined) {
        onLimitChange?.(rowSize);
        onPageChange?.(1);
      }
    },
    [onPageChange, onLimitChange],
  );

  const onChangeSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (searchQueryRef.current) {
      clearTimeout(searchQueryRef.current);
    }
    searchQueryRef.current = setTimeout(() => {
      onSearchQueryChange?.(value);
      onPageChange?.(1);
    }, 300);
  };

  const getEmployerIdsFromSelectedSubOwners = React.useCallback(
    (currentSubOwners: string | Record<string, string | Record<string, string>> | undefined): Set<string> => {
      const employerIds = new Set<string>();
      if (!subOwners) return employerIds;

      const selectedSubOwnerIds =
        currentSubOwners === '*'
          ? subOwners.map(so => so.subOwnerId)
          : Object.keys((currentSubOwners as Record<string, string>) || {});

      subOwners.forEach(subOwner => {
        if (selectedSubOwnerIds.includes(subOwner.subOwnerId)) {
          subOwner.employers?.forEach(employer => {
            employerIds.add(employer.employerId);
            employer.subEmployers?.forEach(subEmp => employerIds.add(subEmp.employerId));
          });
        }
      });

      return employerIds;
    },
    [subOwners],
  );

  const onSelectAll = React.useCallback(() => {
    const currentSelectedData = selectedDataRef.current;
    if (!currentSelectedData) return;

    const shouldSelectAll = currentSelectedData.employers !== '*';

    if (shouldSelectAll) {
      onChangeSelected({
        ...currentSelectedData,
        areAllEmployersSelected: true,
        employers: '*',
        eventFlows: [],
      });
    } else {
      const employerIdsFromSelectedSubOwners = getEmployerIdsFromSelectedSubOwners(currentSelectedData.subOwners);
      const newEmployers: Record<string, string> = {};

      employerIdsFromSelectedSubOwners.forEach(employerId => {
        newEmployers[employerId] = '*';
      });

      onChangeSelected({
        ...currentSelectedData,
        areAllEmployersSelected: false,
        employers: newEmployers,
        eventFlows: [],
      });
    }
  }, [onChangeSelected, getEmployerIdsFromSelectedSubOwners]);

  const onSelectAllOnPage = React.useCallback(() => {
    const currentSelectedData = selectedDataRef.current;
    if (!currentSelectedData) return;

    const allOnPageSelected = employers?.every(employer =>
      isEmployerSelectedHelper(currentSelectedData, employer.employerId),
    );

    const shouldSelectAllOnPage = !allOnPageSelected;

    const newEmployers = getEmployersAsObject(currentSelectedData.employers);
    let newEventFlows = [...(currentSelectedData.eventFlows || [])];

    if (shouldSelectAllOnPage && employers) {
      employers.forEach(employer => {
        newEmployers[employer.employerId] = '*';
        employer.subEmployers?.forEach(subEmp => {
          newEmployers[subEmp.employerId] = '*';
        });
      });
    } else {
      const employerIdsFromSelectedSubOwners = getEmployerIdsFromSelectedSubOwners(currentSelectedData.subOwners);

      employers?.forEach(employer => {
        const idsToRemove = getEmployerWithSubEmployerIds(employer);
        idsToRemove.forEach(id => {
          if (!employerIdsFromSelectedSubOwners.has(id)) {
            delete newEmployers[id];
            newEventFlows = newEventFlows.filter(flowId => flowId !== id);
          }
        });
      });
    }

    onChangeSelected({
      ...currentSelectedData,
      areAllEmployersSelected: false,
      employers: newEmployers,
      eventFlows: newEventFlows,
    });
  }, [employers, onChangeSelected, getEmployerIdsFromSelectedSubOwners]);

  const onSelectEmployer = React.useCallback(
    (employer: ReportEmployer) => () => {
      const currentSelectedData = selectedDataRef.current;
      if (!currentSelectedData) return;

      const isSelected = isEmployerSelectedHelper(currentSelectedData, employer.employerId);
      const newEmployers = getEmployersAsObject(currentSelectedData.employers);
      let newEventFlows = [...(currentSelectedData.eventFlows || [])];

      if (isSelected) {
        const idsToRemove = getEmployerWithSubEmployerIds(employer);
        idsToRemove.forEach(id => delete newEmployers[id]);
        newEventFlows = newEventFlows.filter(id => !idsToRemove.includes(id));
      } else {
        newEmployers[employer.employerId] = '*';
        employer.subEmployers?.forEach(subEmp => {
          newEmployers[subEmp.employerId] = '*';
        });
      }

      onChangeSelected({
        ...currentSelectedData,
        areAllEmployersSelected: false,
        employers: newEmployers,
        eventFlows: newEventFlows,
      });
    },
    [onChangeSelected],
  );

  const onSelectSubEmployer = React.useCallback(
    (parentEmployer: ReportEmployer, subEmployerId: string) => () => {
      const currentSelectedData = selectedDataRef.current;
      if (!currentSelectedData) return;

      const isSubEmployerSelected = isEmployerSelectedHelper(currentSelectedData, subEmployerId);
      const newEmployers = getEmployersAsObject(currentSelectedData.employers);
      let newEventFlows = [...(currentSelectedData.eventFlows || [])];

      if (isSubEmployerSelected) {
        delete newEmployers[subEmployerId];
        newEventFlows = newEventFlows.filter(id => id !== subEmployerId);
      } else {
        newEmployers[subEmployerId] = '*';
        newEmployers[parentEmployer.employerId] = '*';
      }

      onChangeSelected({
        ...currentSelectedData,
        areAllEmployersSelected: false,
        employers: newEmployers,
        eventFlows: newEventFlows,
      });
    },
    [onChangeSelected],
  );

  const onSelectEventFlow = React.useCallback(
    (employerId: string, eventFlowDefaultUser: string | undefined) => () => {
      const currentSelectedData = selectedDataRef.current;
      if (!currentSelectedData) return;

      const currentEventFlows = currentSelectedData.eventFlows || [];
      const isSelected = currentEventFlows.includes(employerId);

      const newEventFlows = isSelected
        ? currentEventFlows.filter(id => id !== employerId)
        : [...currentEventFlows, employerId];

      openConfirmationDialogRef.current(eventFlowDefaultUser != null);

      onChangeSelected({
        ...currentSelectedData,
        eventFlows: newEventFlows,
      });
    },
    [onChangeSelected],
  );

  const renderSubEmployers = React.useCallback(
    (employer: ReportEmployer) => {
      return employer.subEmployers?.map(subEmployer => {
        const isSelected = isEmployerSelectedHelper(selectedData, subEmployer.employerId);
        const isEventFlowSelected = selectedEventFlows?.includes(subEmployer.employerId);

        return (
          <ListRow
            key={`${employer.employerId}_${subEmployer.employerId}`}
            selected={isSelected}
            onSelect={onSelectSubEmployer(employer, subEmployer.employerId)}
            disabled={disabledIfLoadingOrIsLoggedInUser}
            innerRow
          >
            <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={2}>
              <Typography>
                <ActionLink to={`/employers/${subEmployer.employerId}`}>{subEmployer.name}</ActionLink>
              </Typography>
            </Box>
            <Typography sx={{ marginLeft: 'auto', padding: '0 10px 0 0' }}>
              <MuiCheckboxStyled
                color={'secondary'}
                onClick={onSelectEventFlow(subEmployer.employerId, subEmployer.eventFlowDefaultUser)}
                checked={!!isEventFlowSelected && isSelected}
                disabled={!isSelected || disabledIfLoadingOrIsLoggedInUser}
              />
              <Typography variant={'caption'} textTransform={'initial'}>
                <FormattedMessage id={TranslationKeys.users_accessesList_defaultEventFlowUser} />
              </Typography>
            </Typography>
          </ListRow>
        );
      });
    },
    [selectedData, selectedEventFlows, onSelectSubEmployer, onSelectEventFlow, disabledIfLoadingOrIsLoggedInUser],
  );

  return (
    <Box sx={theme => ({ marginTop: theme.spacing(3) })}>
      <UserAccessesListHeader
        headerTextId={TranslationKeys.users_accessesList_employers}
        onChange={onChangeSearchInput}
        isLoading={isLoading}
        disabled={disabledIfLoadingOrIsLoggedInUser}
        onSelectAll={onSelectAll}
        onSelectAllOnPage={onSelectAllOnPage}
        areAllSelected={selectedData?.employers === '*'}
        areAllOnPageSelected={
          !!employers?.length &&
          employers?.every(employer => isEmployerSelectedHelper(selectedData, employer.employerId))
        }
        noData={listData?.length === 0}
        count={selectedEmployerIds.length}
        totalCount={totalItems}
      />

      <EditableFieldFactory
        editing={true}
        field={{
          type: 'checkbox',
          name: 'autoAssignEmployers',
          label: useIntl().formatMessage({ id: TranslationKeys.users_accessesList_autoAssignEmployer }),
        }}
        value={selectedData?.autoAssignEmployers}
        onChange={(event: any) => {
          if (selectedData) {
            onChangeSelected({
              ...selectedData,
              autoAssignEmployers: event.value,
            });
          }
        }}
      />

      <UserAccessesListContainer>
        {isLoading && <ListRowSkeleton />}

        {showData &&
          listData?.map(employer => {
            const isSelected = isEmployerSelectedHelper(selectedData, employer.employerId);
            const isEventFlowSelected = selectedEventFlows?.includes(employer.employerId);
            const hasSubEmployers = employer.subEmployers && employer.subEmployers.length > 0;

            const selectedSubEmployerCount = hasSubEmployers
              ? employer.subEmployers!.filter(subEmp => selectedEmployerIds.includes(subEmp.employerId)).length
              : 0;

            const rowContent = (
              <>
                <Box display={'flex'} flexDirection={'row'} alignItems={'center'} gap={2}>
                  <Typography>
                    <ActionLink to={`/employers/${employer.employerId}`}>{employer.name}</ActionLink>
                  </Typography>
                  {hasSubEmployers && (
                    <Typography variant={'caption'} textTransform={'initial'}>
                      <FormattedMessage
                        id={TranslationKeys.users_accessesList_subEmployers}
                        values={{ count: employer.subEmployers?.length }}
                      />
                    </Typography>
                  )}
                  {selectedSubEmployerCount > 0 && (
                    <Typography variant={'caption'} textTransform={'initial'}>
                      <FormattedMessage
                        id={TranslationKeys.users_accessesList_selectedCountEmployers}
                        values={{ count: selectedSubEmployerCount }}
                      />
                    </Typography>
                  )}
                </Box>
                <Typography sx={{ marginLeft: 'auto', padding: '0 10px 0 0' }}>
                  <MuiCheckboxStyled
                    color={'secondary'}
                    onClick={onSelectEventFlow(employer.employerId, employer.eventFlowDefaultUser)}
                    checked={!!isEventFlowSelected && isSelected}
                    disabled={!isSelected || disabledIfLoadingOrIsLoggedInUser}
                  />
                  <Typography variant={'caption'} textTransform={'initial'}>
                    <FormattedMessage id={TranslationKeys.users_accessesList_defaultEventFlowUser} />
                  </Typography>
                </Typography>
              </>
            );

            if (hasSubEmployers) {
              return (
                <ListCollapsibleRow
                  key={employer.employerId}
                  selected={isSelected}
                  onSelect={onSelectEmployer(employer)}
                  disabled={disabledIfLoadingOrIsLoggedInUser}
                  rowHeader={rowContent}
                >
                  {renderSubEmployers(employer)}
                </ListCollapsibleRow>
              );
            }

            return (
              <ListRow
                key={employer.employerId}
                selected={isSelected}
                onSelect={onSelectEmployer(employer)}
                disabled={disabledIfLoadingOrIsLoggedInUser}
              >
                {rowContent}
              </ListRow>
            );
          })}
        {showNoData && <ListRowNoData searchQuery={externalSearchQuery} />}
      </UserAccessesListContainer>

      {/* Pagination - only show for non-super-admin users with server-side pagination */}
      {!isSuperAdmin && totalPages > 0 && (
        <Pagination
          count={totalItems}
          page={page}
          rowsPerPage={limit}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPaginationChange={onPaginationChange}
        />
      )}
    </Box>
  );
});
